import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Menu, X, CardSim } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../components/Button";
import { Container } from "../components/Container";
import { HashLink } from "../components/HashLink";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { useActiveSection } from "../hooks/useActiveSection";
import { useLocale } from "../hooks/useLocale";
import { useScrollPosition } from "../hooks/useScrollPosition";
import { BRAND } from "../lib/constants";

const NAV_ANCHORS = [
  { key: "features", href: "#features" },
  { key: "how_it_works", href: "#how-it-works" },
  { key: "for_agencies", href: "#for-agencies" },
  { key: "faq", href: "#faq" },
] as const;

// Referentially stable list of section ids for the scroll-spy hook.
const SECTION_IDS = NAV_ANCHORS.map((a) => a.href.slice(1));

export function Navbar() {
  const { t } = useTranslation();
  const { pathFor, code } = useLocale();
  const location = useLocation();
  const scrollY = useScrollPosition();
  const scrolled = scrollY > 40;
  const [mobileOpen, setMobileOpen] = useState(false);
  const reduce = useReducedMotion();
  const activeSection = useActiveSection(SECTION_IDS);
  const blogPath = pathFor("/blog");
  const esimShopPath = pathFor("/esim/shop");
  const isBlogActive =
    location.pathname === blogPath ||
    location.pathname === `/${code}/blog`;
  const isEsimShopActive =
    location.pathname === esimShopPath ||
    location.pathname.startsWith(`/${code}/esim/`);
  const onDarkChrome = false;

  const linkClass = (isActive: boolean) =>
    "relative inline-flex py-1 text-sm font-medium transition-colors duration-150 " +
    (onDarkChrome
      ? isActive
        ? "text-white"
        : "text-white/70 hover:text-white"
      : isActive
        ? "text-text-primary"
        : "text-text-muted hover:text-text-primary");

  const mobileLinkClass = (isActive: boolean) =>
    "block rounded-xl px-4 py-4 text-2xl font-semibold transition-colors " +
    (isActive
      ? "bg-bg-subtle text-text-primary"
      : "text-text-primary hover:bg-bg-subtle");

  const iconBtnClass =
    "inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-150 md:hidden " +
    (onDarkChrome
      ? "border-white/35 bg-white/10 text-white hover:border-white/55 hover:bg-white/18"
      : "border-border-soft text-text-primary");

  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const close = () => setMobileOpen(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, [mobileOpen]);

  const mobileMenu = (
    <AnimatePresence>
      {mobileOpen ? (
        <motion.div
          key="mobile-menu"
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label={t("nav.primary_label")}
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
          animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          style={{ backgroundColor: "var(--color-bg-base)" }}
          className="fixed inset-0 z-[100] flex flex-col md:hidden"
        >
          <div className="flex h-16 items-center justify-between gap-3 px-5 sm:h-20 sm:px-8">
            <Link
              to={pathFor("/")}
              onClick={() => setMobileOpen(false)}
              className="flex min-w-0 items-center"
              aria-label={`${BRAND.name} home`}
            >
              <Logo height={32} className="h-8 w-auto max-w-[min(100%,11rem)] flex-none object-contain sm:h-9" />
            </Link>
            <button
              type="button"
              className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full border border-border-soft text-text-primary"
              aria-label={t("nav.close_menu")}
              onClick={() => setMobileOpen(false)}
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <ul className="flex flex-1 flex-col items-stretch gap-1 px-5 pt-6 sm:px-8">
            {NAV_ANCHORS.map((link) => {
              const id = link.href.slice(1);
              const isActive = !isBlogActive && !isEsimShopActive && activeSection === id;
              return (
                <li key={link.key} className="relative">
                  {isActive ? (
                    <motion.span
                      layoutId="mobile-nav-indicator"
                      className="pointer-events-none absolute inset-y-2 start-0 w-1 rounded-full bg-brand-500"
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 32,
                      }}
                      aria-hidden="true"
                    />
                  ) : null}
                  <HashLink
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    aria-current={isActive ? "location" : undefined}
                    className={mobileLinkClass(isActive)}
                  >
                    {t(`nav.${link.key}`)}
                  </HashLink>
                </li>
              );
            })}
            <li className="relative">
              {isBlogActive ? (
                <motion.span
                  layoutId="mobile-nav-indicator"
                  className="pointer-events-none absolute inset-y-2 start-0 w-1 rounded-full bg-brand-500"
                  transition={{
                    type: "spring",
                    stiffness: 380,
                    damping: 32,
                  }}
                  aria-hidden="true"
                />
              ) : null}
              <Link
                to={blogPath}
                onClick={() => setMobileOpen(false)}
                aria-current={isBlogActive ? "page" : undefined}
                className={mobileLinkClass(isBlogActive)}
              >
                {t("nav.blog", { defaultValue: "Blog" })}
              </Link>
            </li>
            <li>
              <Link
                to={esimShopPath}
                onClick={() => setMobileOpen(false)}
                aria-current={isEsimShopActive ? "page" : undefined}
                className={
                  "mt-2 flex items-center gap-3 rounded-2xl border px-4 py-4 text-2xl font-semibold transition-colors " +
                  (isEsimShopActive
                    ? "border-brand-500 bg-brand-500 text-white"
                    : "border-brand-300 bg-brand-50 text-brand-600 dark:border-brand-500/50 dark:bg-brand-500/15 dark:text-brand-300")
                }
              >
                <CardSim
                  className={
                    "h-6 w-6 flex-none " +
                    (isEsimShopActive ? "text-white" : "text-brand-500 dark:text-brand-300")
                  }
                  aria-hidden="true"
                />
                {t("nav.esim")}
              </Link>
            </li>
          </ul>

          <div className="border-t border-border-soft px-5 py-5 sm:px-8">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-text-muted">
                {t("nav.preferences", { defaultValue: "Preferences" })}
              </p>
              <div className="flex items-center gap-2">
                <LanguageSwitcher />
                <ThemeToggle />
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <header
      className={
        "fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-300 " +
        (scrolled
          ? onDarkChrome
            ? "backdrop-blur-md bg-black/55 border-b border-white/10"
            : "backdrop-blur-md bg-bg-base/75 border-b border-border-soft"
          : "bg-transparent border-b border-transparent")
      }
    >
      <Container>
        <nav
          className="flex h-16 items-center justify-between gap-3 sm:h-20 sm:gap-4"
          aria-label={t("nav.primary_label")}
        >
          <Link
            to={pathFor("/")}
            className="flex shrink-0 items-center"
            aria-label={`${BRAND.name} home`}
          >
            <Logo
              height={36}
              className="h-8 w-auto object-contain sm:h-9 md:h-10"
            />
          </Link>

          <ul className="hidden items-center gap-8 md:flex">
            {NAV_ANCHORS.map((link) => {
              const id = link.href.slice(1);
              const isActive = !isBlogActive && !isEsimShopActive && activeSection === id;
              return (
                <li key={link.key} className="relative">
                  <HashLink
                    to={link.href}
                    aria-current={isActive ? "location" : undefined}
                    className={linkClass(isActive)}
                  >
                    {t(`nav.${link.key}`)}
                    {isActive ? (
                      <motion.span
                        layoutId="nav-underline"
                        className="pointer-events-none absolute -bottom-0.5 left-0 right-0 h-[2px] rounded-full bg-brand-500"
                        transition={
                          reduce
                            ? { duration: 0 }
                            : {
                                type: "spring",
                                stiffness: 380,
                                damping: 32,
                              }
                        }
                        aria-hidden="true"
                      />
                    ) : null}
                  </HashLink>
                </li>
              );
            })}
            <li className="relative">
              <Link
                to={blogPath}
                aria-current={isBlogActive ? "page" : undefined}
                className={linkClass(isBlogActive)}
              >
                {t("nav.blog", { defaultValue: "Blog" })}
                {isBlogActive ? (
                  <motion.span
                    layoutId="nav-underline"
                    className="pointer-events-none absolute -bottom-0.5 left-0 right-0 h-[2px] rounded-full bg-brand-500"
                    transition={
                      reduce
                        ? { duration: 0 }
                        : {
                            type: "spring",
                            stiffness: 380,
                            damping: 32,
                          }
                    }
                    aria-hidden="true"
                  />
                ) : null}
              </Link>
            </li>
            <li className="relative">
              <Link
                to={esimShopPath}
                aria-current={isEsimShopActive ? "page" : undefined}
                className={
                  "group inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-[background-color,border-color,color,box-shadow] duration-150 " +
                  (isEsimShopActive
                    ? "border-brand-500 bg-brand-500 text-white shadow-md"
                    : "border-brand-300 bg-brand-50 text-brand-600 shadow-sm hover:border-brand-400 hover:bg-brand-100 hover:shadow-md dark:border-brand-500/50 dark:bg-brand-500/15 dark:text-brand-300 dark:hover:border-brand-400 dark:hover:bg-brand-500/25")
                }
              >
                <CardSim
                  className={
                    "h-4 w-4 flex-none transition-transform duration-150 group-hover:scale-110 " +
                    (isEsimShopActive ? "text-white" : "text-brand-500 dark:text-brand-300")
                  }
                  aria-hidden="true"
                />
                {t("nav.esim")}
              </Link>
            </li>
          </ul>

          <div className="flex shrink-0 items-center gap-2 md:gap-3">
            <div className="hidden items-center gap-2 md:flex">
              <LanguageSwitcher tone={onDarkChrome ? "onDark" : "default"} />
              <ThemeToggle tone={onDarkChrome ? "onDark" : "default"} />
            </div>

            <HashLink to="#download">
              <Button
                as="button"
                variant="primary"
                size="md"
              >
                {t("nav.download", {
                  defaultValue: "Download",
                })}
              </Button>
            </HashLink>

            <button
              type="button"
              className={iconBtnClass}
              aria-label={t("nav.open_menu")}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </nav>
      </Container>

      {typeof document !== "undefined"
        ? createPortal(mobileMenu, document.body)
        : null}
    </header>
  );
}
